import React, { useState } from 'react';
import { useApi } from '../../hooks/useApi';
import { useAppState } from '../../context/AppStateContext';
import LessonEditor from './LessonEditor';
import LessonCard from './LessonCard';
import {
    ArchiveBoxIcon,
    PlusIcon,
    ChevronRightIcon,
    ArrowUpIcon,
    ArrowDownIcon
} from '@heroicons/react/24/outline';
import PremiumTooltip from '../common/PremiumTooltip';
import Modal from '../common/Modal';

const ModuleCard = ({ 
    module, 
    courseId, 
    onEdit, 
    onDelete, 
    onTogglePublish, 
    onRefresh,
    onMoveUp,
    onMoveDown,
    isFirst,
    isLast
}) => {

    const { del } = useApi();
    const { showToast } = useAppState();
    const [showLessons, setShowLessons] = useState(Boolean(isFirst));
    const [showLessonEditor, setShowLessonEditor] = useState(false);
    const [editingLesson, setEditingLesson] = useState(null);

    const [lessonToDelete, setLessonToDelete] = useState(null);

    const handleAddLesson = () => {
        setEditingLesson(null);
        setShowLessonEditor(true);
    };

    const handleEditLesson = (lesson) => {
        setEditingLesson(lesson);
        setShowLessonEditor(true);
    };

    const handleDeleteLesson = (lesson) => {
        setLessonToDelete(lesson);
    };

    const confirmDeleteLesson = async () => {
        if (!lessonToDelete) return;

        try {
            // [SYNC]: Use the CBC API for sub-strand deletion to support visibility overrides
            // Passing learning_area ensures that National items are hidden rather than deleted.
            await del(`/api/cbc/sub-strands/${lessonToDelete.id}/?learning_area=${courseId}`);
            
            onRefresh();
            showToast('Sub-strand removed successfully!', 'success');
            setLessonToDelete(null);
        } catch (error) {
            console.error('Error deleting sub-strand:', error);
            showToast('Failed to remove sub-strand', 'error');
        }
    };

    const handleManageContent = (lesson) => {
        // Content management is now handled via LessonCard or other triggers
    };

    return (
        <div className="bg-white rounded-2xl border border-slate-100 hover:border-blue-200 transition-all shadow-sm group mb-4">
            <div className="flex items-stretch">
                {/* Reorder Controls */}
                <div className="flex flex-col border-r border-slate-50 bg-slate-50/30 w-10 shrink-0 rounded-l-2xl overflow-hidden">
                    <button
                        onClick={(e) => { e.stopPropagation(); onMoveUp(); }}
                        disabled={isFirst}
                        className={`flex-1 flex items-center justify-center transition-colors ${isFirst ? 'text-slate-200 cursor-not-allowed' : 'text-slate-400 hover:bg-white hover:text-blue-600'}`}
                    >
                        <ArrowUpIcon className="w-4 h-4 stroke-[3]" />
                    </button>
                    <button
                        onClick={(e) => { e.stopPropagation(); onMoveDown(); }}
                        disabled={isLast}
                        className={`flex-1 flex items-center justify-center border-t border-slate-50 transition-colors ${isLast ? 'text-slate-200 cursor-not-allowed' : 'text-slate-400 hover:bg-white hover:text-blue-600'}`}
                    >
                        <ArrowDownIcon className="w-4 h-4 stroke-[3]" />
                    </button>
                </div>

                <div 
                    className="flex-1 p-5 cursor-pointer hover:bg-slate-50/50 transition-colors"
                    onClick={() => setShowLessons(!showLessons)}
                >
                    <div className="flex items-start justify-between">
                        <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                                <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all ${showLessons ? 'bg-[#18216D] text-white' : 'bg-slate-100 text-[#18216D]'}`}>
                                    <ArchiveBoxIcon className="h-6 w-6" />
                                </div>
                                <h3 className="text-lg md:text-xl font-black text-[#18216D] tracking-tight">{module.title}</h3>
                                <ChevronRightIcon
                                    className={`w-5 h-5 text-slate-300 transform transition-transform duration-300 ${showLessons ? 'rotate-90' : ''}`}
                                />
                            </div>
                            {module.description && (
                                <p className="text-sm text-slate-500 mb-4 ml-13 leading-relaxed font-medium">{module.description}</p>
                            )}
                            <div className="flex items-center space-x-6 text-[10px] font-black text-slate-400 ml-13 uppercase tracking-[0.1em]">
                                <span className="flex items-center"><span className="w-1.5 h-1.5 bg-slate-200 rounded-full mr-2"></span>Strand: {module.order}</span>
                                <span className="flex items-center text-[#18216D]"><span className="w-1.5 h-1.5 bg-[#18216D] rounded-full mr-2"></span>{module.sub_strands?.length || 0} Sub-strands</span>
                            </div>
                        </div>

                        {/* Module Actions */}
                        <div className="flex items-center space-x-2 ml-4">
                            <PremiumTooltip text="Edit Strand">
                                <button
                                    onClick={(e) => { e.stopPropagation(); onEdit(module); }}
                                    className="p-2.5 text-blue-600 bg-white border border-slate-100 hover:border-blue-200 hover:bg-blue-50 rounded-xl transition-all shadow-sm"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                    </svg>
                                </button>
                            </PremiumTooltip>
                            <PremiumTooltip text="Remove Strand">
                                <button
                                    onClick={(e) => { e.stopPropagation(); onDelete(module); }}
                                    className="p-2.5 text-rose-600 bg-white border border-slate-100 hover:border-rose-200 hover:bg-rose-50 rounded-xl transition-all shadow-sm"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                </button>
                            </PremiumTooltip>
                        </div>
                    </div>
                </div>
            </div>

            {/* Lessons Section */}
            {showLessons && (
                <div className="p-6 border-t border-slate-50 bg-slate-50/20 ml-10">
                    <div className="flex items-center justify-between mb-4">
                        <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Curriculum Sub-strands</h4>
                        <button
                            onClick={handleAddLesson}
                            className="px-4 py-2 bg-[#18216D] text-white rounded-lg hover:bg-[#0D164F] transition-all text-xs font-bold flex items-center space-x-2 shadow-lg shadow-indigo-900/10"
                        >
                            <PlusIcon className="w-4 h-4 font-black" />
                            <span>Create Sub-strand</span>
                        </button>
                    </div>

                    {module.sub_strands && module.sub_strands.length > 0 ? (
                        <div className="space-y-3">
                            {module.sub_strands.map((lesson) => (
                                <LessonCard
                                    key={lesson.id}
                                    lesson={lesson}
                                    onEdit={handleEditLesson}
                                    onDelete={handleDeleteLesson}
                                    onManageContent={handleManageContent}
                                    onRefresh={onRefresh}
                                    courseId={courseId}
                                />
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-10 bg-white rounded-2xl border-2 border-dashed border-slate-200">
                            <PlusIcon className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                            <p className="text-sm text-slate-500 font-medium">No sub-strands defined yet.</p>
                            <button
                                onClick={handleAddLesson}
                                className="mt-3 text-sm text-[#18216D] hover:text-[#0D164F] font-black"
                            >
                                Add Your First Sub-strand
                            </button>
                        </div>
                    )}
                </div>
            )}

            {/* Lesson Editor Modal */}
            {showLessonEditor && (
                <LessonEditor
                    moduleId={module.id}
                    courseId={courseId}
                    lesson={editingLesson}
                    onClose={() => {
                        setShowLessonEditor(false);
                        setEditingLesson(null);
                    }}
                    onSave={() => {
                        onRefresh();
                        setShowLessonEditor(false);
                        setEditingLesson(null);
                    }}
                />
            )}

            {/* Sub-strand Deletion Modal */}
            <Modal
                isOpen={!!lessonToDelete}
                onClose={() => setLessonToDelete(null)}
                title="Delete Sub-strand"
                type="danger"
                footer={
                    <>
                        <button
                            onClick={confirmDeleteLesson}
                            className="px-10 py-5 bg-rose-500 text-white rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-rose-600 transition-all shadow-xl shadow-rose-900/20"
                        >
                            Delete Permanently
                        </button>
                        <button
                            onClick={() => setLessonToDelete(null)}
                            className="px-10 py-5 bg-slate-100 text-[#18216D] rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-slate-200 transition-all"
                        >
                            Cancel
                        </button>
                    </>
                }
            >
                Are you sure you want to delete <span className="text-rose-600 font-black italic">"{lessonToDelete?.title}"</span>?
                <div className="mt-4 p-4 bg-slate-50 rounded-2xl border border-slate-100 text-xs font-bold text-slate-500 not-italic">
                    <span className="text-rose-600 font-black uppercase tracking-widest block mb-2">Warning: Irreversible Action</span>
                    This will permanently remove the sub-strand and all associated lesson content from this course.
                </div>
            </Modal>
        </div>
    );
};

export default ModuleCard;

